// Stage 12 (Public Get Involved) — pure, React-free URL helpers for the Get
// Involved CTAs. Extracted out of get-involved-hub.tsx so the outbound-CTA URL
// logic (UTM scheme + the `role=` preselect carried by Speak and Sponsor CTAs)
// can be unit-tested in a plain node env without pulling in React /
// @mindstonehq/ui.
//
// The `/interest?...` targets land on community-admin's public interest form.
// Speak and Sponsor CTAs carry a `role=` param to preselect the interest form's
// multiselect (Q1 decision: public interest form, NO event linkage; `role=` is
// revalidated server-side and never determines the stored role or any
// authorization).

// Source values must stay in sync with the Zod enum on community-admin's
// public-get-involved.ts route. Adding a new value here without updating the
// backend will be rejected as a validation error.
export type GetInvolvedSource =
  | "event_page"
  | "rsvp"
  | "website"
  | "newsletter"
  | "slideshow";

// The outbound `/interest` CTAs. Sponsor uses distinct live/card ids so
// `utm_content` preserves which public event surface drove the signup.
export type InterestCta =
  | "associate-signup"
  | "volunteer-signup"
  | "speaker-signup"
  | "sponsor-signup-live"
  | "sponsor-signup-card";

// cta → `role=` preselect on the interest form. Associate/Volunteer intentionally
// send no `role=` so their existing URLs (used by the 4 pre-Stage-12
// GetInvolvedHub routes) are byte-for-byte unchanged.
// Kept in lockstep with community-admin's interest-form allow-list
// (client/src/lib/interest-form-preselect.ts) and its server-side revalidation.
const CTA_PRESELECT_ROLE: Partial<Record<InterestCta, string>> = {
  "speaker-signup": "speaker",
  "sponsor-signup-live": "sponsor",
  "sponsor-signup-card": "sponsor",
};

// Build an outbound `/interest` URL with the shared UTM scheme + optional
// `role=` preselect. Trailing slash on the base is trimmed to avoid
// `https://ca//interest`. The role param (when present) is placed first purely
// for readability; param order is not load-bearing.
export function buildInterestUrl(
  communityUrl: string,
  source: GetInvolvedSource,
  cta: InterestCta,
  refSlug: string,
): string {
  const base = communityUrl.replace(/\/$/, "");
  const params = new URLSearchParams();
  const role = CTA_PRESELECT_ROLE[cta];
  if (role) params.set("role", role);
  params.set("utm_source", "mindstone");
  params.set("utm_medium", source);
  params.set("utm_campaign", "community-amp");
  params.set("utm_content", cta);
  params.set("ref", refSlug);
  return `${base}/interest?${params.toString()}`;
}
